import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {RightBarSection} from '../../section';
import {IResourceGalleryController} from './ResourceGallerySectionController';
import {TsShareMultipleResources} from '../../../ts-share-multiple-resources/component';
import TsResourceCheckbox from '../../../ts-resource-checkbox/ts-resource-checkbox';
import {TsImageShareModes} from '../../../ts-image-share-modes';
import {TsBadgedImage} from '../../../ts-badged-image/component';
import {InteractionSummaryButton} from '../../../interaction-summary/interaction-summary-button';
import {RightBarEvents} from '../../RightBarController';
import Translate from '../../../translate/Translate';
import {ImageLoader} from '../../../image-loader/component';
import './ResourceGallerySection.scss';

export interface IResourceGallerySectionEvents {
    emit(event: RightBarEvents, ...args: any[]): void;
}

export interface IResourceGallerySectionProps {
    ctrl: IResourceGalleryController;
    eventEmitter: IResourceGallerySectionEvents;

    flattenedHistoryRecords: any[];
    screenshots: any[];
    multipleImagesSharingEnabled: boolean;
    isHighImageSharingQuality: boolean;
    enableSharingImagesContainer: boolean;
    resourcesReadyToBeShared: boolean;
    enableCopyToClipboard: boolean;
    enableEmailImageSharing: boolean;
    sharingModeActions: any[];
    dateFormat: string;

    areAllResourcesSelected: (type: string, data: {history: any[]; images: any[]}) => boolean;
    downloadAndEmailSharing: () => boolean;
    enableLocalImageSaving: boolean;
    isActiveResource: (resource: any) => boolean;
    imagesPreviewHeight: () => number;
    imagesPreviewWidth: () => number;
    getImageLayout: (image: any) => string;
    isQuickShareEnabled: () => boolean;
    isSelectResourceReady: (data: any) => boolean;
    isResourceSelected: (resource: any, type: string) => boolean;
    uploadLocalImage?: (file: any) => void;
    failedLocalLoad?: (error: any) => void;
}

@observer
export class ResourceGallerySection extends TechseeBaseComponent<IResourceGallerySectionProps> {
    renderInternal(): JSX.Element {
        const {
            ctrl,
            eventEmitter,
            flattenedHistoryRecords,
            screenshots,
            multipleImagesSharingEnabled,
            isHighImageSharingQuality,
            enableSharingImagesContainer,
            resourcesReadyToBeShared,
            enableCopyToClipboard,
            enableEmailImageSharing,
            sharingModeActions,
            dateFormat,
            areAllResourcesSelected,
            downloadAndEmailSharing,
            enableLocalImageSaving,
            isActiveResource,
            imagesPreviewHeight,
            imagesPreviewWidth,
            getImageLayout,
            isQuickShareEnabled,
            isSelectResourceReady,
            isResourceSelected
        } = this.props;

        if (!ctrl.shouldShow) {
            return <></>;
        }

        const resources = ctrl.getResources();
        const showMultipleResourcesShare = ctrl.getShowMultipleResourcesShare();
        const flattenedHistory = flattenedHistoryRecords;
        const titleData = ctrl.getTitle();
        const emptyStateData = ctrl.getEmptyStateContent();
        const openButtonData = ctrl.getOpenButton();

        const title = (
            <>
                <Translate text={titleData.text} />
                {titleData.selectedCount !== undefined && (
                    <span className={ctrl.resourceType === 'images' ? 'selected-resource-title' : ''}>
                        ({titleData.selectedCount}) <Translate text='DASHBOARD.VIEW.SELECTED' />
                    </span>
                )}
                {titleData.showImageLoader && (
                    <ImageLoader
                        onImageSelect={this.props.uploadLocalImage || (() => {})}
                        onFailedLoad={this.props.failedLocalLoad || (() => {})}
                        useNewInterface={ctrl.useNewInterface}
                    />
                )}
            </>
        );

        return (
            <RightBarSection
                className={ctrl.className}
                title={title}
                isOpen={ctrl.sectionOpen}
                onToggle={ctrl.toggleSection.bind(ctrl)}
                useNewInterface={ctrl.useNewInterface}
                dir={ctrl.dir}
                isHistorySection={ctrl.isHistorySection}
                showToggleIcon={ctrl.getShowToggleIcon?.() ?? true}>
                {emptyStateData && (
                    <div className='panel-empty'>
                        <Translate text={emptyStateData.emptyText || ''} />
                    </div>
                )}

                {openButtonData && (
                    <button
                        onClick={() => {
                            if (ctrl.isHistorySection) {
                                eventEmitter.emit(RightBarEvents.OPEN_CUSTOMER_HISTORY_DIALOG);
                            } else {
                                openButtonData.onClick();
                            }
                        }}
                        style={{textAlign: openButtonData.textAlign || 'left'}}
                        className='btn btn-primary btn-block'
                        type='button'>
                        <Translate text={openButtonData.text} />
                    </button>
                )}

                {showMultipleResourcesShare && (
                    <>
                        {ctrl.resourceType === 'history' && <hr className='row-header' />}
                        <TsShareMultipleResources
                            resourceType={ctrl.resourceType}
                            images={screenshots}
                            history={flattenedHistory}
                            areAllResourcesSelected={(() => {
                                const result = areAllResourcesSelected(ctrl.resourceType, {
                                    history: flattenedHistory,
                                    images: screenshots
                                });

                                return result;
                            })()}
                            selectUnselectAllResources={(
                                resourceType: string,
                                data: {history: any[]; images: any[]},
                                select?: boolean
                            ) => {
                                eventEmitter.emit(RightBarEvents.SELECT_UNSELECT_ALL_RESOURCES, {
                                    resourceType,
                                    data,
                                    select
                                });
                            }}
                            enableLocalImageSaving={enableLocalImageSaving}
                            downloadResources={(resourceType: string, data: {history: any[]; images: any[]}) => {
                                eventEmitter.emit(RightBarEvents.DOWNLOAD_RESOURCES, {resourceType, data});
                            }}
                            resourcesReadyToBeShared={resourcesReadyToBeShared}
                            enableCopyToClipboard={enableCopyToClipboard}
                            copyResourcesToClipboard={(resourceType: string) => {
                                eventEmitter.emit(RightBarEvents.COPY_RESOURCES_TO_CLIPBOARD, resourceType);
                            }}
                            sendResourcesToClient={() => {
                                eventEmitter.emit(RightBarEvents.SEND_RESOURCES_TO_CLIENT, ctrl.resourceType);
                            }}
                            downloadAndEmailSharing={downloadAndEmailSharing()}
                            enableEmailImageSharing={enableEmailImageSharing}
                            sendResourcesByEmail={(resourceType: string) => {
                                eventEmitter.emit(RightBarEvents.SEND_RESOURCES_BY_EMAIL, resourceType);
                            }}
                            downloadResourcesAndOpenEmail={(
                                resourceType: string,
                                data: {history: any[]; images: any[]}
                            ) => {
                                eventEmitter.emit(RightBarEvents.DOWNLOAD_RESOURCES_AND_OPEN_EMAIL, {
                                    resourceType,
                                    data
                                });
                            }}
                        />
                    </>
                )}

                {(() => {
                    const summaries = ctrl.getInteractionSummaries();

                    return (
                        summaries.length > 0 &&
                        summaries.map((preview: any, idx: number) => (
                            <InteractionSummaryButton
                                key={`preview-${idx}`}
                                interactionSummaryData={preview}
                                toggleInteractionSummaryWidget={(data: any) => {
                                    eventEmitter.emit(RightBarEvents.TOGGLE_INTERACTION_SUMMARY_WIDGET, data);
                                }}
                                stepsTranslated=''
                                dateFormat={dateFormat}
                                isFormatted={true}
                            />
                        ))
                    );
                })()}

                {resources.length > 0 && (
                    <ul className='text-center panel-body-sublist'>
                        {ctrl.shouldGroupByDate()
                            ? this.renderGroupedResources(
                                  ctrl,
                                  resources,
                                  multipleImagesSharingEnabled,
                                  isHighImageSharingQuality,
                                  enableSharingImagesContainer
                              )
                            : this.renderFlatResources(
                                  ctrl,
                                  resources,
                                  multipleImagesSharingEnabled,
                                  isHighImageSharingQuality
                              )}
                    </ul>
                )}
            </RightBarSection>
        );
    }

    private renderFlatResources(
        ctrl: IResourceGalleryController,
        resources: any[],
        multipleImagesSharingEnabled: boolean,
        isHighImageSharingQuality: boolean
    ): React.ReactNode {
        const {
            eventEmitter,
            isActiveResource,
            imagesPreviewHeight,
            imagesPreviewWidth,
            getImageLayout,
            isQuickShareEnabled,
            sharingModeActions,
            isSelectResourceReady,
            isResourceSelected
        } = this.props;
        return resources.map((image: any, index: number) => {
            const sharingData = {
                type: 'url',
                url: image.url,
                useOriginalImage: isHighImageSharingQuality,
                _id: image._id,
                _sharingLocation: 'gallery' as const
            };

            return (
                <li
                    key={image.id || index}
                    className={`image-container ${multipleImagesSharingEnabled ? 'allow-multiple-images-sharing' : ''}`}>
                    <TsImageShareModes
                        isActive={isActiveResource(image)}
                        url={image.url}
                        height={imagesPreviewHeight()}
                        width={imagesPreviewWidth()}
                        displayBadge={ctrl.shouldDisplayBadge(image, index)}
                        badgeValue={ctrl.getBadgeValue(image, index)}
                        layoutType={getImageLayout(image)}
                        onImageLoaded={(event: any) => {
                            eventEmitter.emit(RightBarEvents.IMAGE_LOADED, event);
                        }}
                        onClick={() => {
                            eventEmitter.emit(RightBarEvents.SELECT_CLICKED_RESOURCE, {resource: image});
                        }}
                        isQuickShareEnabled={isQuickShareEnabled()}
                        sharingModeActions={sharingModeActions}
                        sharingData={sharingData}
                        isResourceReady={isSelectResourceReady(sharingData)}
                        onShareClick={(e: any, data: any) => {
                            eventEmitter.emit(RightBarEvents.HISTORY_RESOURCES_ON_SHARE_CLICK, {e, data});
                        }}
                        onShareActionClick={(e: any, type: string, data: any) => {
                            eventEmitter.emit(RightBarEvents.HISTORY_RESOURCE_ACTION_CLICK, {e, type, data});
                        }}
                    />

                    {multipleImagesSharingEnabled && (
                        <TsResourceCheckbox
                            resourceType={ctrl.resourceType}
                            resource={image}
                            onCheckboxClicked={(type: string, res: any) => {
                                eventEmitter.emit(RightBarEvents.TOGGLE_RESOURCE_SELECTION, {
                                    resourceType: type,
                                    resource: res
                                });
                            }}
                            isChecked={isResourceSelected(image, ctrl.resourceType)}
                        />
                    )}
                </li>
            );
        });
    }

    private renderGroupedResources(
        ctrl: IResourceGalleryController,
        records: any[],
        multipleImagesSharingEnabled: boolean,
        isHighImageSharingQuality: boolean,
        enableSharingImagesContainer: boolean
    ): React.ReactNode {
        const {
            eventEmitter,
            isActiveResource,
            imagesPreviewHeight,
            imagesPreviewWidth,
            isQuickShareEnabled,
            sharingModeActions,
            isSelectResourceReady,
            isResourceSelected
        } = this.props;
        return records.map((record: any, recordIdx: number) => {
            return (
                <li key={`record-${recordIdx}`}>
                    <br />
                    {(record.images?.length > 0 || record.videos?.length > 0) && (
                        <h6>{ctrl.formatTimestamp(record.timestamp)}</h6>
                    )}

                    {record.images?.map((image: any, imageIdx: number) => {
                        const isTemp = image.storageType === 'TEMP';
                        const sharingData = {
                            type: 'url',
                            url: image.url,
                            useOriginalImage: isHighImageSharingQuality,
                            _id: image._id,
                            _sharingLocation: 'gallery' as const
                        };

                        return (
                            <div
                                key={`image-${imageIdx}`}
                                className={`image-container ${isTemp ? 'temp' : ''} ${multipleImagesSharingEnabled ? 'allow-multiple-resources-sharing' : ''}`}>
                                <TsImageShareModes
                                    isActive={!isTemp && isActiveResource(image)}
                                    url={image.url}
                                    height={imagesPreviewHeight()}
                                    width={imagesPreviewWidth()}
                                    displayBadge={ctrl.shouldDisplayBadge(image, imageIdx, recordIdx)}
                                    badgeValue={ctrl.getBadgeValue(image, imageIdx, recordIdx)}
                                    onClick={() => {
                                        if (!isTemp) {
                                            eventEmitter.emit(RightBarEvents.SELECT_CLICKED_RESOURCE, {
                                                resource: image,
                                                roomId: record.roomId
                                            });
                                        }
                                    }}
                                    isQuickShareEnabled={isQuickShareEnabled()}
                                    sharingModeActions={sharingModeActions}
                                    sharingData={sharingData}
                                    isResourceReady={isSelectResourceReady(sharingData)}
                                    onShareClick={(e: any, data: any) => {
                                        eventEmitter.emit(RightBarEvents.HISTORY_RESOURCES_ON_SHARE_CLICK, {e, data});
                                    }}
                                    onShareActionClick={(e: any, type: string, data: any) => {
                                        eventEmitter.emit(RightBarEvents.HISTORY_RESOURCE_ACTION_CLICK, {
                                            e,
                                            type,
                                            data
                                        });
                                    }}
                                />

                                {multipleImagesSharingEnabled && enableSharingImagesContainer && (
                                    <TsResourceCheckbox
                                        resourceType={ctrl.resourceType}
                                        resource={image}
                                        onCheckboxClicked={(type: string, res: any) => {
                                            eventEmitter.emit(RightBarEvents.TOGGLE_RESOURCE_SELECTION, {
                                                resourceType: type,
                                                resource: res
                                            });
                                        }}
                                        isChecked={isResourceSelected(image, ctrl.resourceType)}
                                    />
                                )}
                            </div>
                        );
                    })}

                    {ctrl.getVideosForRecord(record).map((video: any, videoIdx: number) => {
                        const isTemp = video.storageType === 'TEMP';

                        return (
                            <div
                                key={`video-${videoIdx}`}
                                className={isTemp ? 'temp' : ''}
                                onClick={() => {
                                    if (!isTemp) {
                                        eventEmitter.emit(RightBarEvents.SELECT_CLICKED_RESOURCE, {
                                            resource: video,
                                            roomId: record.roomId
                                        });
                                    }
                                }}>
                                <TsBadgedImage
                                    isActive={!isTemp && isActiveResource(video)}
                                    url={video.url}
                                    height={imagesPreviewHeight()}
                                    width={imagesPreviewWidth()}
                                    displayBadge={true}
                                    badgeValue={String(videoIdx + 1)}
                                />
                            </div>
                        );
                    })}
                </li>
            );
        });
    }
}
